import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useProducts } from "@/hooks/useProducts";

const ProductMarquee = ({ expanded, onToggle }: { expanded?: boolean; onToggle?: () => void }) => {
  const { data: rawProducts } = useProducts(20);
  const products = rawProducts?.filter((p) => !/gift set/i.test(p.node.title));
  const ref = useRef<HTMLDivElement>(null);
  const paused = useRef(false);
  const resumeAt = useRef(0);

  // Gentle auto-scroll that pauses while the shopper interacts
  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0, last = performance.now(), pos = el.scrollLeft;
    const tick = (t: number) => {
      const dt = t - last; last = t;
      if (!paused.current && t > resumeAt.current) {
        if (Math.abs(el.scrollLeft - pos) > 2) pos = el.scrollLeft; // user moved it
        pos += dt * 0.03;
        const half = el.scrollWidth / 2;
        if (pos >= half) pos -= half;
        el.scrollLeft = pos;
      } else pos = el.scrollLeft;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [products]);

  const hold = () => { resumeAt.current = performance.now() + 4000; };
  const nudge = (dir: number) => {
    const el = ref.current; if (!el) return;
    hold();
    const half = el.scrollWidth / 2;
    if (dir < 0 && el.scrollLeft < 300) el.scrollLeft += half;
    el.scrollBy({ left: dir * el.clientWidth * 0.6, behavior: "smooth" });
  };

  if (!products?.length) return null;
  const loop = [...products, ...products];

  return (
    <section aria-label="Atomizer colours" className="py-16 bg-secondary/40 overflow-hidden">
      <p className="text-luxury text-center text-muted-foreground mb-8">Eight colours · One signature</p>
      <div className="relative">
      <button type="button" onClick={() => nudge(-1)} aria-label="Previous colours" className="absolute left-2 md:left-6 top-[40%] -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-background/90 border border-border text-foreground shadow-md flex items-center justify-center hover:bg-background transition-colors">
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button type="button" onClick={() => nudge(1)} aria-label="Next colours" className="absolute right-2 md:right-6 top-[40%] -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-background/90 border border-border text-foreground shadow-md flex items-center justify-center hover:bg-background transition-colors">
        <ChevronRight className="h-5 w-5" />
      </button>
      <div
        ref={ref}
        className="marquee overflow-x-auto no-scrollbar"
        onMouseEnter={() => (paused.current = true)}
        onMouseLeave={() => (paused.current = false)}
        onTouchStart={hold}
        onTouchEnd={hold}
      >
        <div className="flex w-max">
          {loop.map((p, i) => {
            const img = p.node.images.edges[0]?.node;
            return (
              <Link
                key={`${p.node.id}-${i}`}
                to={`/product/${p.node.handle}`}
                aria-hidden={i >= products.length}
                tabIndex={i >= products.length ? -1 : 0}
                className="flex-shrink-0 w-44 md:w-56 mx-4 text-center"
              >
                <div className="aspect-[3/4] rounded-sm overflow-hidden bg-background mb-3">
                  {img && (
                    <img
                      src={`${img.url}&width=500`}
                      alt={`${p.node.title} 10ml vegan leather atomizer`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                  )}
                </div>
                <span className="font-serif text-lg text-foreground">{p.node.title}</span>
              </Link>
            );
          })}
        </div>
      </div>
      </div>
      <div className="text-center mt-10">
        {onToggle ? (!expanded && (
          <button type="button" onClick={onToggle} aria-expanded={expanded} aria-controls="collection" className="btn-luxury">
            Shop All Colours
          </button>
        )) : (
          <Link to="/shop" className="btn-luxury">Shop All Colours</Link>
        )}
      </div>
    </section>
  );
};

export default ProductMarquee;
